import PocketBase from 'pocketbase'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { authTokenValid } from './api'
import { call, interval, useNamedAtom } from './utils'

const pbHost = import.meta.env.VITE_PB_HOST || location.hostname
const pbPort = import.meta.env.VITE_PB_PORT || location.port
export const pbAddr = `${location.protocol}//${pbHost}:${pbPort}`

export const pb = new PocketBase(pbAddr)
pb.autoCancellation(false)
pb.beforeSend = (url, options) => {
  //if (!import.meta.env.PROD) console.log('PB BEFORESEND', url, options)
  return { url, options: { ...options, cache: 'no-store' } }
}
window.pb = pb

export const objectify = records => Object.fromEntries(records.map(v => [v.id, v]))

const isObjectOrArrayOfObjects = v => {
  if (Array.isArray(v)) {
    if (v.length === 0) return false
    else return !!v[0] && typeof v[0] === 'object'
  } else {
    return !!v && typeof v === 'object'
  }
}

export const pbPrep__DEPRECATED__ = value => {
  if (Array.isArray(value)) {
    return objectify(value.map(pbPrep))
  } else if (value && typeof value === 'object' && 'id' in value) {
    for (const key in value.expand) {
      if (!isObjectOrArrayOfObjects(value.expand[key])) continue
      const tmp = value[key]
      value[key] = pbPrep(value.expand[key])
      value.expand[key] = tmp
    }
    return value
  } else {
    return value
  }
}

export const pbPrep = value => {
  if (Array.isArray(value)) {
    return objectify(value.map(pbPrep))
  } else {
    return value
  }
}

export const pbError = e => {
  if (e?.response?.message) {
    let msg = e.response.message
    if (e.response.data) {
      const problems = []
      for (let k in e.response.data) {
        problems.push(k + ' ' + e.response.data[k].message)
      }
      msg += '\n\t' + problems.join(',\n\t')
    }
    return msg
  } else if (e?.originalError?.message) {
    return e.originalError.message
  } else if (e.message) {
    return String(e.message)
  } else {
    return String(e)
  }
}

export const useCollection = (name, expand = undefined) => {
  const expandOpt = expand ? { expand } : {}
  const [coll, setColl] = useNamedAtom('useCollection?name=' + name, [])
  const [loaded, setLoaded] = useNamedAtom('useCollection?loaded=' + name, false)
  useEffect(() => {
    pb.collection(name).getFullList(expandOpt).then(setColl).then(() => setLoaded(true))
    const unsubPromise = pb.collection(name).subscribe('*', ({ action, record }) => {
      if (action === 'delete') {
        setColl(coll => {
          const idx = coll.findIndex(v => v.id === record.id)
          return idx === -1 ? coll : coll.toSpliced(idx, 1)
        })
      } else if (action === 'update') {
        setColl(coll => {
          const idx = coll.findIndex(v => v.id === record.id)
          return idx === -1 ? coll : coll.toSpliced(idx, 1, record)
        })
      } else if (action === 'create') {
        setColl(coll => [...coll, record])
      }
    }, expandOpt)
    return () => unsubPromise.then(call)
  }, [])
  const collMemo = useMemo(() => pbPrep(coll), [coll, loaded])
  return [collMemo, loaded]
}

export const usePaginatedCollection = (collectionName, {
  page = 1,
  perPage = 10,
  filter = '',
  sort = '',
  expand = '',
  requestKey = null,
} = {}) => {
  const [records, setRecords] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loaded, setLoaded] = useNamedAtom('usePaginatedCollection?loaded=' + collectionName, false)

  const fetchPage = useCallback(async () => {
    try {
      const result = await pb.collection(collectionName).getList(page, perPage, {
        filter,
        sort,
        expand,
        requestKey,
      })

      setRecords(result.items)
      setTotalItems(result.totalItems)
      setTotalPages(result.totalPages)
    } catch (e) {
      // console.log("ERR",e)
    } finally {
      setLoaded(true)
    }
  }, [collectionName, page, perPage, filter, sort, expand, requestKey])

  const fetchPageRef = useRef(fetchPage)
  fetchPageRef.current = fetchPage

  useEffect(() => {
    const unsubPromise = pb.collection(collectionName)
      .subscribe('*', () => {
       fetchPageRef.current()
      }, { filter, expand })
      .catch(err => {
        console.error('ERR:', pbError(err))
      })

    return () => {
      unsubPromise.then(call)
    }
  }, [collectionName, filter, expand])

  useEffect(() => {
    fetchPage()
  }, [fetchPage])

  return { records, totalItems, totalPages, loaded, page, perPage, refetch: fetchPage }
}

export const useRecord = (collection, recordId, options = {}) => {
  const [record, setRecord] = useState(undefined)

  useEffect(() => {
    let alive = true
    let unsubscribe = () => {}

    setRecord(undefined)

    const loadRecord = async () => {
      try {
        const found = await pb.collection(collection).getOne(recordId, options)
        if (alive) setRecord(pbPrep(found))
      } catch (err) {
        if (alive) setRecord(null)
      }
    }

    if (recordId) {
      loadRecord()

      // Subscribe with error handling
      const subscribe = async () => {
        try {
          await pb.collection(collection).subscribe(recordId, ({ record }) => {
            if (alive) setRecord(pbPrep(record))
          }, options)
          // Store unsubscribe function
          unsubscribe = () => {
            pb.collection(collection).unsubscribe(recordId).catch(() => {})
          }
        } catch (err) {
          // Ignore 403/404 realtime errors – they don't affect data fetching
          if (err.status !== 403 && err.status !== 404) {
            console.warn(`[useRecord] Subscription error for ${collection}/${recordId}:`, err)
          }
          // Still set record if we have it, but no realtime updates
        }
      }
      subscribe()
    } else {
      setRecord(null)
    }

    return () => {
      alive = false
      unsubscribe()
    }
  }, [collection, recordId])

  return record
}

const passChanged = (prevOrig, currOrig) => {
  return false
}

const PocketbaseUserContext = createContext({ token: undefined, user: undefined, logout: () => { } })

export const PocketbaseUserProvider = ({ children, onNoUser, blocking }) => {
  const [token, setToken] = useNamedAtom('pocketbase.authStore.token', pb?.authStore?.token)
  const [user, setUser] = useNamedAtom('pocketbase.user', pb?.authStore?.record)

  useEffect(() => pb.authStore.onChange(setToken), [])

  useEffect(() => interval(() => {
    authTokenValid().then(valid => {
      if (!valid) pb.authStore.clear()
    })
  }, 1000 * 60, true), [token]) // check every minute
  useEffect(() => {
    if (!pb.authStore?.record?.id) {
      setUser(null)
      return
    }
    pb.collection('users')
      .getOne(pb.authStore?.record?.id)
      .catch(err => {
        setUser(null)
        pb.authStore.clear()
      })
      .then(record => {
        const preped = pbPrep(record)
        setUser(prev => {
          if (passChanged(prev, preped)) {
            setImmediate(() => pb.authStore.clear())
            return prev
          } else {
            return preped
          }
        })
      })
    const unsubPromise = pb.collection('users').subscribe(pb.authStore?.record?.id, ({ record }) => {
      const preped = pbPrep(record)
      setUser(prev => {
        if (passChanged(prev, preped)) {
          setImmediate(() => pb.authStore.clear())
          return prev
        } else {
          return preped
        }
      })
    }).catch(err => {
      setUser(null)
      pb.authStore.clear()
    })
    return () => {
      unsubPromise.then(call)
    }
  }, [token, pb.authStore?.record?.id])

  useEffect(() => { if (!user) onNoUser?.() }, [!user])

  const context = useMemo(() => ({ token, user, logout: () => pb.authStore.clear() }), [user, token])

  return (
    <PocketbaseUserContext value={context}>
      {blocking ? (!!user && children) : children}
    </PocketbaseUserContext>
  )
}

export const useUser = () => useContext(PocketbaseUserContext)

export const pbUpsert = async (collection, value) => {
  const batch = pb.createBatch()
  batch.collection(collection).upsert(value)
  return await batch.send().catch(err => alert(pbError(err)))
}

export const pbDelete = async (collection, id, confirmMsg = 'Удалить, вы уверены?\n Это действие нельзя отменить!') => {
  if (!confirm(confirmMsg))
    return
  return await pb.collection(collection).delete(id)
}